import { ReactElement, useMemo } from 'react';
import { Chip, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import type { ITag } from 'services/api/entities/tag';
import type { TagStore } from 'services/store/tags';
import { useSongsStore, useTagsStore } from 'services/hooks';
import { locations } from 'services/routes/locations';
import useStyles from './style';

type PropsType = {
  tagStore: TagStore;
  selectTag: (tag: ITag) => void;
};

const MobileFiltersBar = (props: PropsType): ReactElement => {
  const { tagStore, selectTag } = props;
  const navigate = useNavigate();
  const classes = useStyles();
  const { t } = useTranslation();
  const { selectedTags } = tagStore;
  const { areTagsOrFiltersApplied, resetTags } = useTagsStore();
  const { resetSongs } = useSongsStore();

  const isSelectedTagsEmpty = useMemo((): boolean => {
    const selectedTagsKeys = Object.keys(selectedTags);
    return (
      selectedTagsKeys.length === 0 ||
      selectedTagsKeys.every(
        (tagType: string) => selectedTags[tagType].length === 0
      )
    );
  }, [selectedTags]);

  const handleClearFilters = (): void => {
    if (areTagsOrFiltersApplied) {
      resetTags();
      resetSongs();
      navigate(locations.general.songList.pathname);
    }
  };

  return (
    <Grid
      container
      className={classes.root}
      sx={{ display: isSelectedTagsEmpty ? 'none' : 'flex' }}
      data-testid="mobile-filters-bar"
    >
      <Grid item xs={8} className={classes.tagFilters}>
        {Object.keys(selectedTags).map((tagType: string) =>
          selectedTags[tagType].map((tag: ITag) => (
            <Chip
              data-testid="songTagChips"
              key={tag.id}
              className={classes.selectedTag}
              label={tag.name}
              size="small"
              variant="outlined"
              color="primary"
              deleteIcon={<CancelIcon />}
              onDelete={() => selectTag(tag)}
            />
          ))
        )}
      </Grid>
      <Grid item xs={4} sx={{ px: 2 }} onClick={() => handleClearFilters()}>
        <Typography
          sx={{ minWidth: 'max-content', textDecoration: 'underline' }}
          variant="body1"
        >
          {t('filtersBar:mobile.clearFilters')}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default MobileFiltersBar;
